<template>
  <div class="login-page">
    <div class="top-bg"></div>
    <div class="overlay"></div>
    <div class="login-card">
      <div class="title">登录</div>
      <el-form ref="loginForm" :model="form" :rules="rules" class="form" @submit.native.prevent>
        <el-form-item prop="username">
          <div class="login-field" :class="{ active: isFieldActive('username') }">
            <div class="field-wrap">
              <div class="field-label">账号</div>
              <div class="field-line"></div>
            </div>
            <el-input v-model.trim="form.username" autocomplete="off" placeholder="" @focus="focusedField = 'username'" @blur="focusedField = ''" @keyup.enter.native="handleLogin" />
          </div>
        </el-form-item>
        <el-form-item prop="password">
          <div class="login-field" :class="{ active: isFieldActive('password') }">
            <div class="field-wrap">
              <div class="field-label">密码</div>
              <div class="field-line"></div>
            </div>
            <el-input v-model.trim="form.password" autocomplete="off" placeholder="" show-password @focus="focusedField = 'password'" @blur="focusedField = ''" @keyup.enter.native="handleLogin" />
          </div>
        </el-form-item>
        <label class="remember-box">
          <input v-model="rememberUsername" type="checkbox" />
          <span>记住账号</span>
        </label>
        <el-button class="submit-btn" type="primary" :loading="loading" @click="handleLogin">
          登录
        </el-button>
      </el-form>
    </div>
  </div>
</template>

<script>
import { enableCasePreview } from '@/utils/auth';
import { TipsPop } from '@/utils/tool';

// 只记住账号，密码不做任何本地留存
const USERNAME_KEY = 'tjweb-login-username';

export default {
  name: 'LoginPage',
  data() {
    return {
      form: {
        username: '',
        password: ''
      },
      focusedField: '',
      loading: false,
      rememberUsername: !!localStorage.getItem(USERNAME_KEY),
      // 校验规则与后端 AuthLoginReqVO 保持一致
      rules: {
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9]{4,30}$/, message: '账号为 4-30 位数字或字母', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 4, max: 16, message: '密码长度为 4-16 位', trigger: 'blur' }
        ]
      }
    };
  },
  created() {
    this.form.username = localStorage.getItem(USERNAME_KEY) || '';
  },
  methods: {
    isFieldActive(field) {
      return this.focusedField === field;
    },
    handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (!valid || this.loading) {
          return;
        }
        this.loading = true;
        try {
          await this.$store.dispatch('userLogin', this.form);
          if (this.rememberUsername) {
            localStorage.setItem(USERNAME_KEY, this.form.username);
          } else {
            localStorage.removeItem(USERNAME_KEY);
          }
          if (this.$route.query.casePreview === '1') {
            enableCasePreview();
          }
          TipsPop({ message: '登录成功', type: 'success' });
          this.$router.replace(this.$route.query.redirect || '/');
        } catch (error) {
          // 具体错误信息已由 api/http.js 的响应拦截器提示
          this.form.password = '';
        } finally {
          this.loading = false;
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.login-page {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 24px;
  overflow: hidden;
  background: #070b12;

  .top-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(7, 11, 18, 0.22), rgba(7, 11, 18, 0.9)), url('../case/img/case-top-bg.png') no-repeat 50% 0;
    background-size: cover;
    transform: scale(1.03);
    animation: bgDrift 18s ease-in-out infinite alternate;
  }

  .overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(5, 10, 18, 0.12), rgba(5, 10, 18, 0.72)), linear-gradient(135deg, rgba(4, 10, 22, 0.64), rgba(7, 18, 36, 0.5) 48%, rgba(3, 8, 17, 0.86));
  }

  .login-card {
    position: relative;
    z-index: 3;
    width: 408px;
    max-width: 408px;
    padding: 38px 32px 30px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.045)), rgba(255, 255, 255, 0.055);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 14px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.24);
    backdrop-filter: blur(18px) saturate(135%);
    -webkit-backdrop-filter: blur(18px) saturate(135%);
    animation: cardIn 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .login-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 32px;
    right: 32px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
  }

  .title {
    color: #fff;
    font-size: 34px;
    font-weight: 600;
    letter-spacing: 4px;
    text-indent: 4px;
    text-align: left;
    animation: titleIn 0.7s 0.16s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .form {
    margin-top: 32px;
    animation: formIn 0.7s 0.26s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  ::v-deep .el-form-item {
    margin-bottom: 26px;
    position: relative;
  }

  .login-field {
    position: relative;
    display: grid;
    grid-template-columns: 62px 1fr;
    align-items: center;
    min-height: 58px;
    padding: 0 16px;
    background: rgba(255, 255, 255, 0.075);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    box-shadow: none;
    transition: border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, padding 0.25s ease, transform 0.34s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .field-wrap {
    position: relative;
    top: auto;
    left: auto;
    z-index: 1;
    padding-left: 0;
    pointer-events: none;
    transform: translateY(0);
    transition: padding 0.28s ease, transform 0.34s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .field-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 15px;
    letter-spacing: 0;
    transform-origin: left center;
    transition: color 0.25s ease, transform 0.34s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
  }

  .field-line {
    width: 0;
    height: 1px;
    margin-top: 1px;
    background: rgba(255, 255, 255, 0.75);
    opacity: 0;
    transition: width 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease;
  }

  .login-field.active .field-wrap {
    padding-left: 0;
    transform: translateY(0);
  }

  .login-field.active .field-label {
    color: #fff;
    opacity: 1;
    transform: scale(0.84) translateX(2px);
  }

  .login-field.active .field-line {
    width: 28px;
    opacity: 1;
  }

  .login-field ::v-deep .el-input {
    grid-column: 2;
  }

  .login-field.active ::v-deep .el-input {
    grid-column: 1 / -1;
  }

  ::v-deep .el-input__inner {
    height: 56px;
    padding: 0 16px 0 0;
    color: #fff;
    font-size: 16px;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    transition: padding 0.3s ease;
  }

  .login-field.active ::v-deep .el-input__inner {
    height: 38px;
    padding: 0;
  }

  ::v-deep .el-input__inner::placeholder {
    color: transparent;
  }

  ::v-deep .el-input__inner:focus {
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  .login-field.active {
    grid-template-columns: 1fr;
    align-items: start;
    gap: 2px;
    padding: 8px 16px 7px;
    background: rgba(255, 255, 255, 0.11);
    border-color: rgba(255, 255, 255, 0.34);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }

  ::v-deep .el-input__suffix {
    color: rgba(255, 255, 255, 0.72);
    right: 12px;
  }

  .remember-box {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0 24px;
    color: rgba(255, 255, 255, 0.82);
    font-size: 14px;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .remember-box:hover {
    color: #fff;
  }

  .remember-box input {
    accent-color: #fff;
    transform-origin: center;
    transition: transform 0.2s ease;
  }

  .remember-box input:checked {
    transform: scale(1.08);
  }

  .submit-btn {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 52px;
    font-size: 16px;
    border: none;
    letter-spacing: 3px;
    font-weight: 600;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.88);
    color: #0d2542;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
    transition: box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }

  ::v-deep .submit-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 42%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
    transform: skewX(-20deg);
    transition: left 0.55s ease;
  }

  ::v-deep .submit-btn:hover,
  ::v-deep .submit-btn:focus {
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.24);
    transform: translateY(-2px);
  }

  ::v-deep .submit-btn:hover::before,
  ::v-deep .submit-btn:focus::before {
    left: 120%;
  }

  ::v-deep .submit-btn:active {
    transform: translateY(0) scale(0.99);
  }
}

@keyframes bgDrift {
  0% {
    transform: scale(1.03) translate3d(0, 0, 0);
  }

  100% {
    transform: scale(1.07) translate3d(-18px, -10px, 0);
  }
}

@keyframes cardIn {
  0% {
    opacity: 0;
    transform: translateY(24px) scale(0.98);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes titleIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes formIn {
  0% {
    opacity: 0;
    transform: translateY(14px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .login-page {
    min-height: 100vh;
    height: auto;
    padding: 18px;

    .top-bg {
      background-position: 58% 0;
    }

    .login-card {
      width: calc(100% - 30px);
      max-width: 408px;
      padding: 30px 20px 24px;
      border-radius: 10px;
    }

    .login-card::before {
      left: 20px;
      right: 20px;
    }

    .title {
      font-size: 30px;
      letter-spacing: 4px;
      text-indent: 4px;
    }

    .form {
      margin-top: 24px;
    }

    ::v-deep .el-form-item {
      margin-bottom: 20px;
    }

    .login-field,
    .login-field.active {
      min-height: 54px;
      padding-left: 14px;
      padding-right: 14px;
    }

    .field-label {
      font-size: 14px;
    }

    ::v-deep .el-input__inner {
      height: 52px;
      font-size: 15px;
    }

    .login-field.active ::v-deep .el-input__inner {
      height: 36px;
    }

    .remember-box {
      margin: 2px 0 20px;
      font-size: 13px;
    }

    .submit-btn {
      height: 48px;
      font-size: 15px;
      letter-spacing: 2px;
    }
  }
}

@media (max-width: 420px) {
  .login-page {
    padding: 12px;

    .top-bg {
      transform: scale(1.01);
    }

    .login-card {
      padding: 24px 16px 20px;
    }

    .login-card::before {
      left: 16px;
      right: 16px;
    }

    .title {
      font-size: 24px;
      letter-spacing: 2px;
      text-indent: 2px;
    }

    .login-field {
      grid-template-columns: 54px 1fr;
    }
  }
}
</style>
